import { Dispatch, FC, SetStateAction, useState } from "react";
import Table from "../../../assets/components/Table/Table";
import IoloLicenseKeysSubRow from "../IoloLicenseKeysSubRow/IoloLicenseKeysSubRow";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { IoloDetailInfo } from "../../../redux/RTKQuery/api";
import moment from "moment";
import CopyToClipboard from "../../../assets/components/CopyToClipboard/CopyToClipboard";
import ImageSprite from "../../../assets/components/ImageSprite/ImageSprite";
import ArrowSprite from "../../../assets/components/Table/Img/arrow_sprite.svg";
import ButtonArrow from "../../../assets/components/ButtonArrow/ButtonArrow";
import { SearchBarDataHandler } from "../../../assets/components/SearchBar/SearchBar";
import TableColumnVisibilityMenu from "../../../assets/components/Table/TableColumnVisibilityMenu";

const formatUnix = (ts: number | null | undefined): string =>
    ts ? moment.unix(ts).format("LLL") : "—";

interface Props {
    dataTable: IoloDetailInfo[];
    onSort: Dispatch<SetStateAction<SearchBarDataHandler>>;
}

const IoloLicenseKeysTable: FC<Props> = ({ dataTable, onSort }) => {
    const [sortNextRebillDate, setSortNextRebillDate] = useState<
        boolean | undefined
    >(undefined);

    const defaultColumnVisibility: VisibilityState = {
        productName: false,
    };

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
        defaultColumnVisibility,
    );

    const handleResetColumnVisibility = () => {
        setColumnVisibility(defaultColumnVisibility);
    };

    const handleSort = (state: boolean | undefined) => {
        let newState = state;
        if (state === undefined) newState = true;
        if (state === true) newState = false;
        if (state === false) newState = undefined;

        setSortNextRebillDate(newState);

        onSort((prevData) => {
            return {
                ...prevData,
                sortNextRebillDate: newState,
            };
        });
    };

    const columns: ColumnDef<IoloDetailInfo>[] = [
        {
            id: "productName",
            header: "Product Name",
            accessorFn: ({ product }) => product?.title ?? "—",
            size: 180,
        },
        {
            id: "dateCreated",
            header: "Date Created",
            accessorFn: ({ ioloInfo, license }) =>
                formatUnix(ioloInfo?.createdDate ?? license?.creationDate),
            size: 160,
        },
        {
            id: "lastRebillDate",
            header: () => <span>Last Rebill date</span>,
            accessorKey: "lastRebillDate",
            size: 160,
            accessorFn: ({ license }) => formatUnix(license?.lastRebillDate),
        },
        {
            header: () => (
                <ButtonArrow
                    text="Next Rebill date"
                    state={sortNextRebillDate}
                    onClick={() => handleSort(sortNextRebillDate)}
                />
            ),
            accessorKey: "nextRebillDate",
            size: 160,
            accessorFn: ({ license }) => formatUnix(license?.nextRebillDate),
        },
        {
            id: "orderId",
            header: "Order ID",
            accessorFn: ({ license }) => license?.orderId ?? "—",
            size: 160,
        },
        {
            header: "License Key",
            accessorFn: ({ ioloInfo, license }) =>
                ioloInfo?.licenseKey ?? license?.key ?? "",
            size: 200,
            cell: ({ row }) => {
                const key =
                    row.original.ioloInfo?.licenseKey ??
                    row.original.license?.key ??
                    "";
                return key ? <CopyToClipboard text={key} /> : <span>—</span>;
            },
        },
        {
            header: "Status",
            accessorFn: ({ ioloInfo }) => ioloInfo?.licenseState ?? "—",
            size: 120,
            minSize: 100,
            cell: ({ row }) => (
                <span>{row.original.ioloInfo?.licenseState ?? "—"}</span>
            ),
        },
        {
            id: "expander",
            header: () => null,
            size: 30,
            cell: ({ row }) => {
                const arrow = row.getIsExpanded() ? "arrowUp" : "arrowDown";
                return (
                    <button
                        className="edit_button"
                        {...{
                            onClick: () => row.getToggleExpandedHandler(),
                            style: { cursor: "pointer" },
                        }}
                    >
                        <ImageSprite iconSprite={`${ArrowSprite}#${arrow}`} />
                    </button>
                );
            },
        },
    ];

    const renderSubComponent = ({ row }: { row: Row<IoloDetailInfo> }) => (
        <IoloLicenseKeysSubRow
            {...row.original}
            key={row.original.ioloInfo?.licenseKey ?? row.original.license?.id}
        />
    );

    return (
        <Table<IoloDetailInfo>
            className="key_checker_table"
            data={dataTable}
            columns={columns}
            getRowCanExpand={() => true}
            renderSubComponent={renderSubComponent}
            enableColumnVisibility={true}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            renderTopToolbar={(table) => (
                <TableColumnVisibilityMenu
                    table={table}
                    onReset={handleResetColumnVisibility}
                />
            )}
        />
    );
};

export default IoloLicenseKeysTable;
